import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';

import { getCellReducers } from './helpers/getCellReducers';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  useDeleteSubscriptionPlanPrice,
  useGetSubscriptionPlanPrices,
} from '../../../services/redux/api/subscriptionsApi';
import { ISubscriptionPlanPrice } from '../../../types/interfaces/subscription-plan-price';
import { useAccess } from '../../../hooks/useAccess';
import { FormikProps, useFormik } from 'formik';

export function useSubscriptionPlanPricesComponent(classes) {
  const { t } = useTranslation();
  const { data: planPrices } = useGetSubscriptionPlanPrices();
  const [itemForDelete, setItemForDelete] =
    useState<ISubscriptionPlanPrice>(null);
  const [itemForEditing, setItemForEditing] =
    useState<ISubscriptionPlanPrice>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteSubscriptionPlanPrice] = useDeleteSubscriptionPlanPrice();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      current: allOption,
    },
    onSubmit: () => {},
  });
  const filteredValues = useMemo(
    () =>
      planPrices?.filter((plan: ISubscriptionPlanPrice) => {
        const currentFilterId = filterFormik.values.current?.id ?? 'all';
        const filterValue = filter.toLowerCase();
        const isCurrentText = plan.current
          ? t('common.yes').toLowerCase()
          : t('common.no').toLowerCase();

        if (
          !String(plan.stripePriceId ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !plan.value?.toString().includes(filterValue) &&
          !isCurrentText.includes(filterValue)
        ) {
          return false;
        }

        if (currentFilterId !== 'all') {
          const isCurrent = !!plan.current;
          if (
            (currentFilterId === true && !isCurrent) ||
            (currentFilterId === false && isCurrent)
          ) {
            return false;
          }
        }

        return true;
      }),
    [planPrices, filter, filterFormik.values, t],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.subscriptionPlanPrices?.canEdit ?? false,
      canDelete: access?.subscriptionPlanPrices?.canDelete ?? false,
    },
    (plan: ISubscriptionPlanPrice) => {
      setItemForEditing(plan);
      setShowEditModal(true);
    },
    (plan: ISubscriptionPlanPrice) => {
      setItemForDelete(plan);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.SUBSCRIPTION_PLAN_PRICE_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteSubscriptionPlanPrice({ id: itemForDelete.id });
  }

  const filterCurrentOptions = [
    allOption,
    { id: true, name: t('common.yes') },
    { id: false, name: t('common.no') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterCurrentOptions,
  };
}
